import { useAppStore } from '@core/store'
import { clashKey, type ClashResult } from '@viewer/clash/lib'
import { prepareElement, pointInsideMesh, type PreparedElement } from '@viewer/clash/lib/narrowPhase'
import { IDENTITY_PLACEMENT } from '@viewer/lib/geoTransform'
import { getPsetsAndQsets, getType } from '@viewer/lib/ifcItemData'
import { lerConjuntosDosTypes, lerElementos } from '@viewer/lib/psetsHerdados'
import { extractSpatialOrigins } from '@viewer/lib/spatialPlacements'
import { viewerApi } from '@viewer/lib/viewerApi'
import { collectWorldTriangles, type WorldTrianglesSource } from '@viewer/lib/worldTriangles'
import { resolveClashGuids } from './clashBcfExport'
import type {
  ClashElementoInfo,
  ClashPca,
  ClashParaClassificar,
  NivelDeReferencia,
  V3,
} from './clashClassificacao'
import { REDES, type Especialidade, type MapaDeEspecialidades } from './clashEspecialidades'

// ---------------------------------------------------------------------------
// O adaptador do viewer para a classificacao: le dos modelos abertos o
// que as regras precisam de cada elemento e de cada conflito.
//
//   - nome, ObjectType e nome do type (o "tipo" Revit);
//   - Overall Size, Outside Diameter, Size, Diameter e System Name, da
//     instancia ou do type (o diametro exterior das redes);
//   - a geometria em mundo: caixa, eixos principais (PCA dos vertices) e
//     volume da malha;
//   - o volume comum dos dois elementos, por amostragem com o mesmo
//     pointInsideMesh do motor, so nos pares em que uma regra o usa
//     (EST x ARQ, e rede x ARQ com interpenetracao a contar);
//   - os niveis de referencia: os IfcBuildingStorey do modelo ARQ (ou do
//     que tiver mais pisos), a cota em mundo.
//
// Tudo passa para o referencial das regras: Z para CIMA (o do IFC), com a
// cota igual a altura da cena.
// ---------------------------------------------------------------------------

/** Cena (Y para cima) -> regras (Z para cima). */
function zUp(x: number, y: number, z: number): V3 {
  return [x, -z, y]
}

/** Eixos principais de uma nuvem de pontos (Jacobi 3x3), ordenados do
 *  mais comprido para o mais curto. */
export function pcaDosPontos(p: ArrayLike<number>): ClashPca | null {
  const n = Math.floor(p.length / 3)
  if (n < 2) return null
  const c: V3 = [0, 0, 0]
  for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) c[k] += p[i * 3 + k] / n
  const C = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ]
  for (let i = 0; i < n; i++) {
    const d = [p[i * 3] - c[0], p[i * 3 + 1] - c[1], p[i * 3 + 2] - c[2]]
    for (let a = 0; a < 3; a++) for (let b = 0; b < 3; b++) C[a][b] += d[a] * d[b]
  }
  const V = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ]
  const A = C.map((r) => r.slice())
  for (let sweep = 0; sweep < 50; sweep++) {
    let off = 0
    for (let a = 0; a < 3; a++) for (let b = a + 1; b < 3; b++) off += A[a][b] ** 2
    if (off < 1e-18) break
    for (let a = 0; a < 3; a++)
      for (let b = a + 1; b < 3; b++) {
        if (Math.abs(A[a][b]) < 1e-30) continue
        const th = 0.5 * Math.atan2(2 * A[a][b], A[b][b] - A[a][a])
        const cs = Math.cos(th)
        const sn = Math.sin(th)
        for (let k = 0; k < 3; k++) {
          const akA = A[k][a]
          const akB = A[k][b]
          A[k][a] = cs * akA - sn * akB
          A[k][b] = sn * akA + cs * akB
        }
        for (let k = 0; k < 3; k++) {
          const aA = A[a][k]
          const aB = A[b][k]
          A[a][k] = cs * aA - sn * aB
          A[b][k] = sn * aA + cs * aB
        }
        for (let k = 0; k < 3; k++) {
          const vA = V[k][a]
          const vB = V[k][b]
          V[k][a] = cs * vA - sn * vB
          V[k][b] = sn * vA + cs * vB
        }
      }
  }
  const eixos = [0, 1, 2].map((j) => [V[0][j], V[1][j], V[2][j]] as V3)
  const ext = eixos.map((ax) => {
    let lo = Infinity
    let hi = -Infinity
    for (let i = 0; i < n; i++) {
      const t = (p[i * 3] - c[0]) * ax[0] + (p[i * 3 + 1] - c[1]) * ax[1] + (p[i * 3 + 2] - c[2]) * ax[2]
      if (t < lo) lo = t
      if (t > hi) hi = t
    }
    return { lo, hi }
  })
  const ordem = [0, 1, 2].sort((x, y) => ext[y].hi - ext[y].lo - (ext[x].hi - ext[x].lo))
  return {
    center: c,
    axes: ordem.map((j) => eixos[j]) as [V3, V3, V3],
    lo: ordem.map((j) => ext[j].lo) as V3,
    hi: ordem.map((j) => ext[j].hi) as V3,
  }
}

/** Volume de uma malha fechada (teorema da divergencia), m3. */
export function volumeDaMalha(pos: ArrayLike<number>, idx: ArrayLike<number>): number {
  let v = 0
  for (let t = 0; t + 2 < idx.length; t += 3) {
    const a = idx[t] * 3
    const b = idx[t + 1] * 3
    const c = idx[t + 2] * 3
    const ax = pos[a], ay = pos[a + 1], az = pos[a + 2]
    const bx = pos[b], by = pos[b + 1], bz = pos[b + 2]
    const cx = pos[c], cy = pos[c + 1], cz = pos[c + 2]
    v += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx)
  }
  return Math.abs(v) / 6
}

/** Volume comum por amostragem numa grelha (no maximo ~4000 pontos na
 *  caixa comum), como na analise do Cha139. */
export function volumeComum(A: PreparedElement, B: PreparedElement): number {
  const lo = [0, 1, 2].map((i) => Math.max(A.min[i], B.min[i]))
  const hi = [0, 1, 2].map((i) => Math.min(A.max[i], B.max[i]))
  const ext = [0, 1, 2].map((i) => Math.max(hi[i] - lo[i], 0))
  if (ext.some((e) => e <= 0)) return 0
  const vol = ext[0] * ext[1] * ext[2]
  const cell = Math.max(Math.cbrt(vol / 4000), 0.005)
  const n = ext.map((e) => Math.max(1, Math.ceil(e / cell)))
  const step = ext.map((e, i) => e / n[i])
  let both = 0
  const p = [0, 0, 0]
  for (let i = 0; i < n[0]; i++)
    for (let j = 0; j < n[1]; j++)
      for (let k = 0; k < n[2]; k++) {
        p[0] = lo[0] + (i + 0.5) * step[0]
        p[1] = lo[1] + (j + 0.5) * step[1]
        p[2] = lo[2] + (k + 0.5) * step[2]
        if (pointInsideMesh(p, A) && pointInsideMesh(p, B)) both++
      }
  return both * step[0] * step[1] * step[2]
}

const PROPS = ['Overall Size', 'Outside Diameter', 'Size', 'Diameter', 'System Name']

function texto(v: unknown): string | null {
  if (typeof v === 'string') return v
  const o = v as { value?: unknown } | null
  return typeof o?.value === 'string' ? o.value : null
}

/** Os niveis de referencia: os pisos do modelo ARQ (ou do que tem mais
 *  pisos), com a cota em mundo. */
export async function niveisDeReferencia(especialidades: MapaDeEspecialidades): Promise<NivelDeReferencia[]> {
  const st = useAppStore.getState()
  const candidatos = [...st.models].sort(
    (a, b) => Number(especialidades[b.id] === 'ARQ') - Number(especialidades[a.id] === 'ARQ'),
  )
  let melhor: NivelDeReferencia[] = []
  for (const m of candidatos) {
    let org
    try {
      org = await extractSpatialOrigins(m.id)
    } catch {
      continue
    }
    if (!org) continue
    const p = st.geoOverrides[m.id] ?? st.geoBaselines[m.id] ?? IDENTITY_PLACEMENT
    const niveis = org.items
      .filter((n) => n.ifcType === 'IFCBUILDINGSTOREY' && n.name)
      .map((n) => ({ nome: n.name!.trim(), cota: p.orthogonalHeight + (p.scale || 1) * n.origin[2] }))
    if (especialidades[m.id] === 'ARQ' && niveis.length > 0) return niveis
    if (niveis.length > melhor.length) melhor = niveis
  }
  return melhor
}

export interface ProgressoDaDescricao {
  fase: 'propriedades' | 'geometria' | 'volumes'
  feitos: number
  total: number
}

/**
 * Descreve os conflitos para a classificacao. Devolve, por chave, o
 * conflito pronto a classificar e os GlobalIds dos dois elementos.
 */
export async function descreverConflitos(
  results: readonly ClashResult[],
  especialidades: MapaDeEspecialidades,
  opts: { profundidadeM?: number; onProgress?: (p: ProgressoDaDescricao) => void; signal?: { aborted: boolean } } = {},
): Promise<{ itens: Map<string, ClashParaClassificar>; guids: Record<string, [string | null, string | null]> }> {
  const porModelo = new Map<string, Set<number>>()
  const categoria = new Map<string, string>()
  for (const r of results) {
    for (const [m, id, cat] of [
      [r.pair.modelA, r.pair.localIdA, r.categoryA],
      [r.pair.modelB, r.pair.localIdB, r.categoryB],
    ] as const) {
      const s = porModelo.get(m) ?? new Set<number>()
      s.add(id)
      porModelo.set(m, s)
      if (cat) categoria.set(`${m}:${id}`, cat.toUpperCase())
    }
  }
  const guids = await resolveClashGuids(results)
  const infos = new Map<string, ClashElementoInfo>()
  const preparados = new Map<string, PreparedElement>()
  const total = [...porModelo.values()].reduce((n, s) => n + s.size, 0)
  let feitos = 0
  // Origem comum para as posicoes em float32 (modelos georreferenciados).
  const origem: [number, number, number] = (() => {
    const r = results[0]
    return r ? [r.point[0], r.point[1], r.point[2]] : [0, 0, 0]
  })()
  let indice = 0
  for (const [modelId, set] of porModelo) {
    if (opts.signal?.aborted) break
    const model = viewerApi.getModel(modelId)
    const ids = [...set]
    const esp: Especialidade | null = especialidades[modelId] ?? null
    // Propriedades (instancia e type).
    const dados = new Map<number, { nome: string; objectType: string | null; typeName: string | null; props: Record<string, string> }>()
    if (model) {
      try {
        for (let i = 0; i < ids.length; i += 200) {
          const lote = ids.slice(i, i + 200)
          const itens = (await lerElementos(model as never, lote)) as Record<string, unknown>[]
          const tipos = new Map<number, number>()
          itens.forEach((it, k) => {
            const t = it ? getType(it) : null
            if (t?.localId != null) tipos.set(lote[k], t.localId)
          })
          const conjuntosDosTypes = await lerConjuntosDosTypes(model as never, [...tipos.values()])
          itens.forEach((it, k) => {
            const id = lote[k]
            const props: Record<string, string> = {}
            const grupos = [
              ...(it ? getPsetsAndQsets(it).psets : []),
              ...(conjuntosDosTypes.get(tipos.get(id) ?? -1)?.psets ?? []),
            ]
            for (const g of grupos) for (const r of g.rows) if (PROPS.includes(r.key) && !(r.key in props)) props[r.key] = r.value
            const tipo = it ? getType(it) : null
            dados.set(id, {
              nome: texto(it?.Name) ?? '',
              objectType: texto(it?.ObjectType),
              typeName: tipo?.name && tipo.name !== '-' ? tipo.name : null,
              props,
            })
          })
          feitos += lote.length
          opts.onProgress?.({ fase: 'propriedades', feitos, total })
        }
      } catch (err) {
        console.warn('[clash] propriedades para a classificação', err)
      }
    }
    // Geometria em mundo.
    if (model) {
      try {
        const tri = await collectWorldTriangles(model as unknown as WorldTrianglesSource, {
          ids,
          origin: origem,
          maxTriangles: 20_000_000,
        })
        for (const it of tri.items) {
          const n = it.positions.length / 3
          const pts = new Float64Array(n * 3)
          let min: V3 = [Infinity, Infinity, Infinity]
          let max: V3 = [-Infinity, -Infinity, -Infinity]
          for (let i = 0; i < n; i++) {
            const q = zUp(
              it.positions[i * 3] + origem[0],
              it.positions[i * 3 + 1] + origem[1],
              it.positions[i * 3 + 2] + origem[2],
            )
            pts[i * 3] = q[0]
            pts[i * 3 + 1] = q[1]
            pts[i * 3 + 2] = q[2]
            min = [Math.min(min[0], q[0]), Math.min(min[1], q[1]), Math.min(min[2], q[2])]
            max = [Math.max(max[0], q[0]), Math.max(max[1], q[1]), Math.max(max[2], q[2])]
          }
          const k = `${modelId}:${it.localId}`
          const d = dados.get(it.localId)
          infos.set(k, {
            modelId,
            localId: it.localId,
            especialidade: esp,
            categoria: categoria.get(k) ?? '',
            nome: d?.nome ?? '',
            typeName: d?.typeName ?? null,
            objectType: d?.objectType ?? null,
            props: d?.props ?? {},
            volume: volumeDaMalha(it.positions, it.indices),
            bbox: n > 0 ? { min, max } : null,
            pca: pcaDosPontos(pts),
          })
          preparados.set(k, prepareElement({ index: indice++, positions: it.positions, indices: it.indices }))
        }
        opts.onProgress?.({ fase: 'geometria', feitos, total })
      } catch (err) {
        console.warn('[clash] geometria para a classificação', err)
      }
    }
    // Elementos sem geometria lida: so o que se sabe.
    for (const id of ids) {
      const k = `${modelId}:${id}`
      if (infos.has(k)) continue
      const d = dados.get(id)
      infos.set(k, {
        modelId,
        localId: id,
        especialidade: esp,
        categoria: categoria.get(k) ?? '',
        nome: d?.nome ?? '',
        typeName: d?.typeName ?? null,
        objectType: d?.objectType ?? null,
        props: d?.props ?? {},
      })
    }
  }
  // Conflitos e volume comum (so onde uma regra o usa).
  const prof = opts.profundidadeM ?? 0.01
  const itens = new Map<string, ClashParaClassificar>()
  let v = 0
  const precisaDeVolume = (a: ClashElementoInfo, b: ClashElementoInfo, r: ClashResult) => {
    if (r.kind !== 'hard') return false
    const ea = a.especialidade
    const eb = b.especialidade
    if ((ea === 'EST' && eb === 'ARQ') || (ea === 'ARQ' && eb === 'EST')) return true
    const redeArq = (ea === 'ARQ' && eb && REDES.has(eb)) || (eb === 'ARQ' && ea && REDES.has(ea))
    return !!redeArq && (r.depth ?? 0) >= prof
  }
  for (const r of results) {
    if (opts.signal?.aborted) break
    const ka = `${r.pair.modelA}:${r.pair.localIdA}`
    const kb = `${r.pair.modelB}:${r.pair.localIdB}`
    const a = infos.get(ka)
    const b = infos.get(kb)
    if (!a || !b) continue
    const key = clashKey(r)
    const g = guids[key]
    if (g) {
      a.guid = g[0]
      b.guid = g[1]
    }
    let both: number | null = null
    if (precisaDeVolume(a, b, r)) {
      const pa = preparados.get(ka)
      const pb = preparados.get(kb)
      if (pa && pb) both = volumeComum(pa, pb)
      v++
      if (v % 50 === 0) {
        opts.onProgress?.({ fase: 'volumes', feitos: v, total: results.length })
        await new Promise((res) => setTimeout(res, 0))
      }
    }
    itens.set(key, {
      a,
      b,
      kind: r.kind,
      depth: r.depth ?? null,
      distance: r.distance ?? null,
      point: zUp(r.point[0], r.point[1], r.point[2]),
      both,
    })
  }
  return { itens, guids }
}
